import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Target, Building2, UserPen } from "lucide-react";
import Wrapper from "@/components/shared/Wrapper";
import { cn } from "@/lib/utils";
import { useUser } from "@/stores/authStore";
import { useCandidateManagementStore } from "@/features/candidatesManagment/store/useCandidateManagementStore";
import { CANDIDATE_MODAL_KEYS } from "@/features/candidatesManagment/constants/constant";

function ActionIcon({ variant }) {
  const common = "text-white shrink-0";
  const size = 16;
  if (variant === "target") {
    return <Target size={size} className={common} strokeWidth={2} />;
  }
  if (variant === "building") {
    return <Building2 size={size} className={common} strokeWidth={2} />;
  }
  if (variant === "profile") {
    return <UserPen size={size} className={common} strokeWidth={2} />;
  }
  return <Plus size={size} className={common} strokeWidth={2} />;
}

export default function DashboardQuickActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useUser();
  const isAgency = Boolean(user?.isAgency);
  const { setActiveModal } = useCandidateManagementStore();

  const actions = useMemo(() => {
    const base = [
      {
        action: "add-candidate",
        iconVariant: "plus",
        label: t("dashboardPage.quickActions.addCandidate.label"),
        description: t("dashboardPage.quickActions.addCandidate.description"),
        highlight: false,
      },
      {
        action: "create-position",
        iconVariant: "target",
        label: t("dashboardPage.quickActions.createPosition.label"),
        description: t("dashboardPage.quickActions.createPosition.description"),
        highlight: false,
      },
      {
        action: "add-user",
        iconVariant: "plus",
        label: t("dashboardPage.quickActions.addUser.label"),
        description: t("dashboardPage.quickActions.addUser.description"),
        highlight: false,
      },
    ];

    const fourth = isAgency
      ? {
          action: "add-client",
          iconVariant: "building",
          label: t("dashboardPage.quickActions.addClient.label"),
          description: t("dashboardPage.quickActions.addClient.description"),
          highlight: true,
        }
      : {
          action: "edit-profile",
          iconVariant: "profile",
          label: t("dashboardPage.quickActions.editProfile.label"),
          description: t("dashboardPage.quickActions.editProfile.description"),
          highlight: false,
        };

    return [...base, fourth];
  }, [t, isAgency]);

  const handleAction = (action) => {
    if (action === "add-candidate") {
      navigate("/candidates-managment");
      setActiveModal(CANDIDATE_MODAL_KEYS.CREATE_CANDIDATE);
      return;
    }
    if (action === "create-position") {
      navigate("/positions/new");
      return;
    }
    if (action === "add-user") {
      navigate("/settings");
      return;
    }
    if (action === "add-client") {
      navigate("/clients-board/new");
      return;
    }
    if (action === "edit-profile") {
      navigate("/settings");
    }
  };

  return (
    <Wrapper className="relative overflow-hidden p-5 gap-3 border border-[#E8EDF2]/80 bg-[linear-gradient(165deg,rgba(7,102,152,0.04)_0%,transparent_55%)] shadow-[0_1px_0_rgba(15,23,42,0.06)]">
      <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-[#076698]/[0.07] blur-2xl" aria-hidden />
      <h2 className="relative text-xl font-bold text-foreground mb-3 font-[Dubai] tracking-tight">
        {t("dashboardPage.quickActions.title")}
      </h2>
      <ul className="relative flex flex-col gap-2.5 list-none p-0 m-0">
        {actions.map((item) => (
          <li key={item.action}>
            <button
              type="button"
              onClick={() => handleAction(item.action)}
              className={cn(
                "group flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-start transition-all duration-200",
                "border-[#EEF1F5] bg-white/90 hover:bg-white hover:border-[#076698]/25 hover:shadow-[0_8px_24px_-12px_rgba(7,102,152,0.35)]",
                item.highlight &&
                  "border-[#076698]/30 bg-white ring-1 ring-[#076698]/12"
              )}
            >
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-[1.04]",
                  item.highlight
                    ? "bg-[#076698] shadow-[0_4px_14px_-4px_rgba(7,102,152,0.65)]"
                    : "bg-[#076698]"
                )}
              >
                <ActionIcon variant={item.iconVariant} />
              </div>
              <div className="min-w-0 flex-1 text-start">
                <p className="text-sm font-semibold text-foreground font-[Dubai] leading-snug">
                  {item.label}
                </p>
                <p className="text-xs text-secondary-foreground leading-relaxed mt-0.5 text-start">
                  {item.description}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </Wrapper>
  );
}
